"use client";

import { useQueryClient } from "@tanstack/react-query";
import { LogOut, Settings } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { createQueryString } from "@/lib/utils";
import { useI18n } from "@/locales/client";
import { useTRPC } from "@/trpc/client";
import { createClient } from "@/utils/supabase/client";

interface UserDropDownProps {
  firstName: string;
  lastName: string;
  profileImg: string | null;
  email: string | null;
}
const UserDropdown = ({
  firstName,
  lastName,
  profileImg,
  email,
}: UserDropDownProps) => {
  const pathname = usePathname();
  const [isLoading, setLoading] = useState(false);
  const supabase = createClient();
  const router = useRouter();
  const queryClient = useQueryClient();
  const t = useI18n();
  const trpc = useTRPC();

  const handleLogout = async () => {
    setLoading(true);
    await supabase.auth.signOut({
      scope: "local",
    });
    router.refresh();
    queryClient.invalidateQueries(trpc.users.getPlanLimit.queryOptions());
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Avatar className="h-7 w-7 cursor-pointer">
          <AvatarImage alt="Profile Image" src={profileImg ?? undefined} />
          <AvatarFallback>{firstName ? firstName[0] : "L"}</AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={4} className="w-56">
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <Avatar className="h-8 w-8 rounded-lg">
              <AvatarImage src={profileImg ?? undefined} alt={firstName} />
              <AvatarFallback className="rounded-lg">
                {firstName ? firstName[0] : "L"}
              </AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">
                {firstName} {lastName}
              </span>
              <span className="truncate text-xs">{email}</span>
            </div>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild className="text-sm">
            <Link
              href={`/settings/account?${createQueryString("next", pathname)}`}
              className="flex items-center gap-2"
            >
              <Settings className="size-4" /> {t("userDropdown.settings")}
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogout}
          className="flex items-center gap-2 text-sm"
        >
          <LogOut className="size-4" />
          {isLoading ? t("userDropdown.loading") : t("userDropdown.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserDropdown;
